/**
 * Decode scheduler: mounts large-tier <img> elements for the planned pages ONE AT A TIME and only
 * reveals each after `img.decode()` resolved (a missing decode shows the 's' placeholder underneath,
 * never white). Pages dropped from the plan are evicted by unmounting their <img> (the blob URL is
 * kept). After the app returns to the foreground (WebKit discards decoded data while suspended) the
 * shown images are re-decoded, visible ones first.
 *
 * A failed decode (iPad memory pressure, app suspended mid-decode, mount timeout) blocks that URL
 * until the page re-enters the plan (at most MAX_ATTEMPTS tries per foreground session) or the app
 * returns to the foreground, which clears every failure.
 */
import { useEffect, useState, useSyncExternalStore } from 'react';

export interface LargeImgState {
  url: string;
  /** false while decoding (mounted with opacity 0). */
  visible: boolean;
}

const EMPTY: readonly LargeImgState[] = Object.freeze([]);
const MOUNT_TIMEOUT_MS = 5000;
/** Decode tries per URL before it stays on the 's' placeholder (until the next resume()). */
export const MAX_DECODE_ATTEMPTS = 2;

type Job = { kind: 'decode'; page: number; url: string } | { kind: 'redecode'; page: number };

export class DecodeScheduler {
  private desired: number[] = [];
  private urlFor: (page: number) => string | null = () => null;
  private shown = new Map<number, string>();
  private pending: { page: number; url: string } | null = null;
  /** URLs whose last decode failed (skipped until retried). */
  private failed = new Set<string>();
  /** Failed decode attempts per URL since the last resume(). */
  private attempts = new Map<string, number>();
  private redecode = new Set<number>();
  private elements = new Map<string, HTMLImageElement>();
  private waiters = new Map<string, (el: HTMLImageElement | null) => void>();
  private working = false;
  private disposed = false;
  private listeners = new Set<() => void>();
  private perPage = new Map<number, readonly LargeImgState[]>();
  private version = 0;

  readonly subscribe = (fn: () => void): (() => void) => {
    this.listeners.add(fn);
    return () => this.listeners.delete(fn);
  };

  readonly getVersion = (): number => this.version;

  /** Large images to render for a page (stable array identity while unchanged). */
  imagesFor(page: number): readonly LargeImgState[] {
    return this.perPage.get(page) ?? EMPTY;
  }

  /** Called by <LargeImage> when its element mounts (el) or unmounts (null). */
  readonly registerElement = (url: string, el: HTMLImageElement | null): void => {
    if (el) {
      this.elements.set(url, el);
      const w = this.waiters.get(url);
      if (w) {
        this.waiters.delete(url);
        w(el);
      }
    } else if (this.elements.get(url)) {
      this.elements.delete(url);
    }
  };

  setPlan(desired: number[], urlFor: (page: number) => string | null): void {
    if (this.disposed) return;
    const before = new Set(this.desired);
    this.desired = desired;
    this.urlFor = urlFor;
    const want = new Set(desired);
    // A page that re-enters the plan may retry a failed decode (bounded by MAX_DECODE_ATTEMPTS).
    for (const page of desired) {
      if (before.has(page)) continue;
      const url = urlFor(page);
      if (url && this.failed.has(url) && (this.attempts.get(url) ?? 0) < MAX_DECODE_ATTEMPTS) this.failed.delete(url);
    }
    let changed = false;
    for (const page of [...this.shown.keys()]) {
      if (!want.has(page)) {
        this.shown.delete(page);
        this.redecode.delete(page);
        changed = true;
      }
    }
    if (this.pending && (!want.has(this.pending.page) || urlFor(this.pending.page) !== this.pending.url)) {
      this.cancelPending();
      changed = true;
    }
    if (changed) this.publish();
    this.kick();
  }

  /** Foreground again: re-decode every shown image (in plan order) and retry failed decodes. */
  resume(): void {
    for (const page of this.shown.keys()) this.redecode.add(page);
    this.failed.clear();
    this.attempts.clear();
    this.kick();
  }

  /** Has this URL's decode failed (and not been cleared for a retry yet)? */
  hasFailed(url: string): boolean {
    return this.failed.has(url);
  }

  /** (Re)activate after mount — StrictMode mounts, unmounts and mounts again. */
  attach(): void {
    this.disposed = false;
    this.publish();
    this.kick();
  }

  detach(): void {
    this.disposed = true;
    this.pending = null;
    for (const w of this.waiters.values()) w(null);
    this.waiters.clear();
  }

  private cancelPending(): void {
    if (!this.pending) return;
    const w = this.waiters.get(this.pending.url);
    this.waiters.delete(this.pending.url);
    this.pending = null;
    w?.(null);
  }

  private nextJob(): Job | null {
    for (const page of this.desired) {
      if (this.redecode.has(page) && this.shown.has(page)) return { kind: 'redecode', page };
    }
    this.redecode.clear();
    for (const page of this.desired) {
      const url = this.urlFor(page);
      if (!url || this.failed.has(url) || this.shown.get(page) === url) continue;
      return { kind: 'decode', page, url };
    }
    return null;
  }

  private kick(): void {
    if (!this.working && !this.disposed) void this.work();
  }

  private async work(): Promise<void> {
    this.working = true;
    try {
      for (;;) {
        if (this.disposed) return;
        const job = this.nextJob();
        if (!job) return;
        if (job.kind === 'redecode') {
          this.redecode.delete(job.page);
          const url = this.shown.get(job.page);
          const el = url ? this.elements.get(url) : undefined;
          if (el) await el.decode().catch(() => undefined);
          continue;
        }
        await this.decodeOne(job.page, job.url);
      }
    } finally {
      this.working = false;
    }
  }

  private async decodeOne(page: number, url: string): Promise<void> {
    this.pending = { page, url };
    this.publish();
    const el =
      this.elements.get(url) ??
      (await new Promise<HTMLImageElement | null>((resolve) => {
        const timer = setTimeout(() => {
          if (this.waiters.get(url) === done) this.waiters.delete(url);
          resolve(null);
        }, MOUNT_TIMEOUT_MS);
        const done = (e: HTMLImageElement | null) => {
          clearTimeout(timer);
          resolve(e);
        };
        this.waiters.set(url, done);
      }));
    if (this.disposed) return;
    const stillWanted = () => this.pending?.page === page && this.pending.url === url && this.urlFor(page) === url;
    if (!el) {
      if (stillWanted()) {
        this.markFailed(url);
        this.pending = null;
        this.publish();
      }
      return;
    }
    let ok = true;
    try {
      await el.decode();
    } catch {
      ok = false;
    }
    if (this.disposed) return;
    if (!stillWanted()) return; // plan changed meanwhile; setPlan already cleared pending
    this.pending = null;
    if (ok) {
      this.shown.set(page, url);
      this.attempts.delete(url);
    } else this.markFailed(url);
    this.publish();
  }

  private markFailed(url: string): void {
    this.failed.add(url);
    this.attempts.set(url, (this.attempts.get(url) ?? 0) + 1);
  }

  private publish(): void {
    const next = new Map<number, readonly LargeImgState[]>();
    const pages = new Set<number>([...this.shown.keys(), ...(this.pending ? [this.pending.page] : [])]);
    for (const page of pages) {
      const list: LargeImgState[] = [];
      const s = this.shown.get(page);
      if (s) list.push({ url: s, visible: true });
      if (this.pending?.page === page && this.pending.url !== s) list.push({ url: this.pending.url, visible: false });
      const prev = this.perPage.get(page);
      const same =
        prev && prev.length === list.length && prev.every((p, i) => p.url === list[i]!.url && p.visible === list[i]!.visible);
      next.set(page, same ? prev : Object.freeze(list));
    }
    this.perPage = next;
    this.version++;
    for (const fn of this.listeners) fn();
  }
}

/** One scheduler per viewer instance; re-decodes on visibilitychange→visible and pageshow. */
export function useDecodeScheduler(): DecodeScheduler {
  const [scheduler] = useState(() => new DecodeScheduler());
  useSyncExternalStore(scheduler.subscribe, scheduler.getVersion);
  useEffect(() => {
    const onVis = () => {
      if (document.visibilityState === 'visible') scheduler.resume();
    };
    const onShow = () => scheduler.resume();
    document.addEventListener('visibilitychange', onVis);
    window.addEventListener('pageshow', onShow);
    return () => {
      document.removeEventListener('visibilitychange', onVis);
      window.removeEventListener('pageshow', onShow);
    };
  }, [scheduler]);
  useEffect(() => {
    scheduler.attach();
    return () => scheduler.detach();
  }, [scheduler]);
  return scheduler;
}
